/**
 * Wire contracts for SKU-target health probes (#2584): gateway
 * `/internal/target-probes*` and the ops-api `/v1/ops/target-probes*` proxy.
 *
 * Enable/interval live on `guaranteed_service_entries` SKU rows
 * (`probe_enabled`, `probe_interval_minutes`). An absent or off SKU row
 * means probes are off for that SKU. Probes are independent of the
 * four-tuple circuit breaker. `trip_reason` is `'probe'` (a run of failed
 * rounds: the outage signal), `'quality'` (the leg's bad-shot share over the
 * SKU's quality window, #2658), `'chain'` (#2589), `'jev'` (#2585), or
 * `'silent'` (#3135 / #3139; silent lives on `routing_leg_faults` and is
 * surfaced on the legs listing as `faults[]`).
 * `'probe'` and `'quality'` are both the probe mechanism (`probe_mode`).
 */

export type TargetProbeLayer = 'default' | 'config';

export type TargetProbeTripReason = 'probe' | 'quality' | 'chain' | 'jev' | 'silent';

/** Probe level of a region × physical leg (#2658 follow-up): 常规 / 警戒 / 战争. */
export type ProbeLevel = 'normal' | 'alert' | 'war';

/**
 * Why a level changed. `off`: the war's SKUs switched probes off mid-war;
 * `removed`: the leg left the region's probe listing (its SKUs' probes off,
 * target disabled or deleted); `reset`: an operator reset the leg in this
 * region (red-team P2-6: its level, war count and cooldown are forgotten
 * with its trips) — all three close the level to normal. `signature`: one
 * upstream 4xx whose error type / code is server-side
 * (`GUARANTEED_PROBE_SERVER_FAULT_SIGNATURES`, #2658 tuning) — the
 * signature fast lane. `breaker`: the circuit breaker moved the leg from
 * closed to open on a run of upstream 5xx at least its consecutive
 * threshold long (#2658 staging; a run with any 429 / 408 / timeout /
 * transport failure never). The breaker state is fleet-shared in JP and
 * per host in CN, so like the real-traffic line it passes the two-host
 * gate, the cooldown, memo and single flight. Additive.
 */
export type ProbeLevelSource = 'traffic' | 'jev' | 'signature' | 'breaker' | 'timer' | 'calm' | 'cooldown_block' | 'off' | 'removed' | 'reset';

/**
 * Signature fast lane (#2658 tuning, 88api 2026-09-22: every failure was an
 * HTTP 400 with `type: server_error`, at 1–2 requests a minute — the 60 s
 * line took 8 to 38 minutes). ONE upstream 4xx (not 408 / 429) whose error
 * `type` or `code` equals one of these (case-insensitive, exact) is a war
 * trigger on its leg: not gated by the two-host rule, still by the war
 * cooldown / backoff and the per-process memo and single flight. Kept
 * deliberately small: in 14 days of production the set matched only the
 * 88api outage (79 shots), nothing else. NOT `upstream_error`: aggregators
 * wrap client faults in it (full history: dragoncode 400 / 404 context
 * overflow, ucloud 400 unsupported parameter with code `Bad Request`).
 */
export const GUARANTEED_PROBE_SERVER_FAULT_SIGNATURES = [
  'server_error',
  'api_error',
  'internal_error',
  'internal_server_error',
] as const;

/**
 * Never a signature trigger, even when the other label matches: client-side
 * and rate-limit classes. Anything outside the server set above is never a
 * trigger either; this list decides a response whose type and code disagree.
 */
export const GUARANTEED_PROBE_SIGNATURE_EXCLUDED = [
  'invalid_request_error',
  'authentication_error',
  'permission_error',
  'not_found_error',
  'rate_limit_error',
  'overloaded_error',
  'request_too_large',
  'billing_error',
  'insufficient_quota',
  'rate_limit_exceeded',
  'context_length_exceeded',
  'invalid_api_key',
  // ucloud: 400 `upstream_error` + code `Bad Request` for an unsupported parameter.
  'bad request',
] as const;

/**
 * The matched server-side label of one upstream 4xx (not 408 / 429) whose
 * type or code is in {@link GUARANTEED_PROBE_SERVER_FAULT_SIGNATURES} and
 * neither is in {@link GUARANTEED_PROBE_SIGNATURE_EXCLUDED}; else null.
 */
export function guaranteedProbeServerFaultSignature(response: {
  readonly status: number;
  readonly type: string | null | undefined;
  readonly code: string | null | undefined;
}): string | null {
  if (response.status < 400 || response.status >= 500 || response.status === 408 || response.status === 429) return null;
  const labels = [response.type, response.code]
    .filter((label): label is string => typeof label === 'string')
    .map((label) => label.trim().toLowerCase())
    .filter((label) => label.length > 0);
  if (labels.some((label) => (GUARANTEED_PROBE_SIGNATURE_EXCLUDED as readonly string[]).includes(label))) return null;
  return labels.find((label) => (GUARANTEED_PROBE_SERVER_FAULT_SIGNATURES as readonly string[]).includes(label)) ?? null;
}

/**
 * `details_json` of a `probe_level` load event (sku_id NULL; the four-tuple
 * and region columns set; one row per level change per region × physical
 * leg). The patrol consumes this shape.
 */
export interface ProbeLevelEventDetails {
  readonly from: ProbeLevel;
  readonly to: ProbeLevel;
  readonly source: ProbeLevelSource;
  readonly physical_model: string;
  readonly trigger: {
    readonly failures: number | null;
    readonly window_s: number | null;
    readonly jev_verdict_id: string | null;
    /** Signature fast lane only (source `signature`): the matched type / code and the upstream status. Additive. */
    readonly signature?: string;
    readonly status?: number;
  } | null;
  readonly cooldown_until: string | null;
  /**
   * Wars counted toward the cooldown backoff (k): +1 per war, back to 1 only
   * after `probe_war_backoff_reset_minutes` with no war and no trigger
   * (cooldown-blocked triggers included).
   */
  readonly war_entries_window: number;
  readonly waves: ReadonlyArray<{ readonly shots: number; readonly failed: number }> | null;
  /**
   * How a war ended, on every event whose `from` is war (null otherwise):
   * `tripped` (two bad waves in a row tripped the leg), `calm` (good waves
   * ended it), `timer` (maximum reached, probes off, leg removed),
   * `aborted` (its runner died — deploy, crash — and the lease lapsed: no
   * verdict, so the war does not count toward the cooldown backoff and
   * starts no cooldown; red-team P3-3, additive). The second abort in a
   * row counts and cools like a finished war: `cooldown_until` is then set
   * (red-team P2-1). `source` keeps its meaning (`timer` for an aborted war).
   */
  readonly outcome: ProbeWarEndOutcome | null;
  /** The war's duration in seconds when `outcome` is set, else null. */
  readonly seconds: number | null;
}

export type ProbeWarEndOutcome = 'tripped' | 'calm' | 'timer' | 'aborted';

/** `details_json` of a `probe_war_blocked` load event: a trigger held back by the war cooldown. */
export interface ProbeWarBlockedEventDetails {
  readonly cooldown_until: string;
  readonly war_entries_window: number;
  readonly source: 'traffic' | 'jev' | 'signature' | 'breaker';
  readonly physical_model: string;
}

/**
 * Release damping (#2658 staging rehearsal F3). An upstream that rejects
 * only the real request shape answers the ping probe, so the probe round
 * releases a chain / Jev trip after `probe_recover_ok_rounds` clean rounds
 * and the chain trips it again seconds later — every few minutes. A chain /
 * Jev re-trip within `FLAP_WINDOW` minutes of a release (probe round or the
 * 30-minute expiry) is the n-th flap in a row; the next probe release then
 * also waits `min(BASE × 2^(n−1), MAX)` minutes after that re-trip. n goes
 * back to 1 after `RESET` minutes without a re-trip. Region × member row;
 * derived from this region's load events (no Redis). The 30-minute expiry
 * still lifts a held trip.
 */
export const PROBE_RELEASE_FLAP_WINDOW_MINUTES = 10;
export const PROBE_RELEASE_HOLD_BASE_MINUTES = 5;
export const PROBE_RELEASE_HOLD_MAX_MINUTES = 60;
export const PROBE_RELEASE_FLAP_RESET_MINUTES = 60;

/** `details.d3_damping` of a chain / Jev `target_probe_trip` that followed a release (a flap). */
export interface ProbeReleaseDampingDetails {
  /** This re-trip is the n-th flap in a row (1 = first). */
  readonly flap: number;
  /** The release it followed, and how that release happened. */
  readonly released_at: string;
  readonly release_source: 'probe' | 'expiry';
  readonly seconds_after_release: number;
  /** The next probe release waits this long after this trip (on top of the clean rounds)… */
  readonly hold_minutes: number;
  /** …until then. */
  readonly hold_until: string;
  /**
   * Whether a probe release applies at all: the SKU's `probe_mode` was act
   * at the trip. False: only the `PROBE_EXTERNAL_TRIP_EXPIRY_MINUTES` expiry
   * lifts the trip and the hold is moot. Absent on events written before
   * this field (additive).
   */
  readonly probe_release?: boolean;
}

/**
 * Minutes a chain / Jev trip stands before it expires and the leg is judged
 * live again. It runs from the trip, like a release hold, so a hold at or
 * above it never delays anything: the expiry lifts the trip first.
 */
export const PROBE_EXTERNAL_TRIP_EXPIRY_MINUTES = 30;

/** `min(BASE × 2^(flap−1), MAX)` minutes. */
export function probeReleaseHoldMinutes(flap: number): number {
  const exponent = Math.min(Math.max(0, Math.floor(flap) - 1), 20);
  return Math.min(PROBE_RELEASE_HOLD_BASE_MINUTES * (2 ** exponent), PROBE_RELEASE_HOLD_MAX_MINUTES);
}

/** Trip reasons owned by the probe mechanism (`probe_mode`). */
export const PROBE_OWNED_TRIP_REASONS = ['probe', 'quality'] as const;

export function isProbeOwnedTripReason(value: unknown): value is 'probe' | 'quality' {
  return value === 'probe' || value === 'quality';
}

export interface TargetProbeStateRecord {
  readonly layer: TargetProbeLayer;
  readonly target_id: string;
  readonly sku_id: string;
  readonly sku_name: string;
  readonly upstream_id: string;
  readonly account_id: string;
  readonly key_bucket_id: string;
  readonly upstream_channel_id: string;
  readonly last_probe_at: string | null;
  readonly last_ok_at: string | null;
  readonly consecutive_failed_rounds: number;
  readonly consecutive_ok_rounds: number;
  readonly tripped: boolean;
  readonly trip_reason: TargetProbeTripReason | null;
  readonly shadow_trip_reason: TargetProbeTripReason | null;
  readonly shadow_tripped_at: string | null;
  readonly immune_until: string | null;
  readonly region: string;
  readonly last_status_code: number | null;
  readonly last_error_excerpt: string | null;
  readonly backoff_level: number;
  readonly next_probe_at: string | null;
  readonly updated_at: string;
  /** When `tripped` was last set (0079). Null while not tripped. */
  readonly tripped_at: string | null;
  /** At war (the concurrent-wave burst) until then; null when not at war (0084). */
  readonly war_until?: string | null;
  /** Waves fired so far, and the last wave's failed / judged shots. */
  readonly war_waves?: number | null;
  readonly war_last_failed?: number | null;
  readonly war_last_shots?: number | null;
}

export interface ListTargetProbeStateResponse {
  readonly states: readonly TargetProbeStateRecord[];
}

export interface TargetProbeTargetRef {
  readonly layer: TargetProbeLayer;
  readonly target_id: string;
  /**
   * Region whose probe rows to act on (`jp` / `cn`). Probe state is
   * region-local: ops-api sends `cn` to the Shanghai gateway, and a gateway
   * refuses a region it does not serve (`TARGET_PROBE_REGION_MISMATCH`).
   * Omitted means the receiving gateway's own region.
   */
  readonly region?: string;
}

export interface ResetTargetProbeRequest extends TargetProbeTargetRef {
  /** 0..1440. Omitted means 5. 0 clears immunity. */
  readonly immunity_minutes?: number;
}

/**
 * Why a routing target can (or cannot) take traffic, read off the ladder in
 * `docs/routing/behavior-tree.md` §1 against current assignment state.
 *
 * Reachable: `override` (tier 0, engaged unexpired emergency override),
 * `global` (tier 4, the assigned global route config), `org_assigned`
 * (tier 1, config bound to at least one org), `default_fallthrough`
 * (tier 5 SKU default target; no global config, or an `inherit` global that
 * does not cover the SKU).
 *
 * Unreachable: `unassigned_config` (a config nothing routes to),
 * `shadowed_by_global_exclusive` (SKU default target under a global config
 * that is `exclusive` or covers the SKU, so tier 4 always answers first).
 */
export type TargetProbeReachReason =
  | 'override'
  | 'global'
  | 'org_assigned'
  | 'default_fallthrough'
  | 'unassigned_config'
  | 'shadowed_by_global_exclusive';

export const TARGET_PROBE_REACHABLE_REASONS: readonly TargetProbeReachReason[] = [
  'override',
  'global',
  'org_assigned',
  'default_fallthrough',
];

export function isReachableTargetProbeReason(reason: TargetProbeReachReason): boolean {
  return TARGET_PROBE_REACHABLE_REASONS.includes(reason);
}

export interface TargetProbeLegMember {
  readonly layer: TargetProbeLayer;
  readonly target_id: string;
  readonly enabled: boolean;
  /** Routing weight; only members with weight > 0 can take the first pick. */
  readonly weight: number;
  /**
   * Routing `retry_order` (>= 1; null sorts last). With no weight > 0 leg
   * live the first pick is the chain head by this order
   * (behavior-tree §3b). Omitted by older gateways.
   */
  readonly retry_order?: number | null;
  readonly last_probe_at: string | null;
  readonly last_status_code: number | null;
  /** Owning routing config; null on the default layer. Optional for older gateways. */
  readonly config_id?: string | null;
  readonly config_name?: string | null;
  /** `routing_configs.deprecated_at` is set. Always false on the default layer. */
  readonly config_deprecated?: boolean;
  /** Org assignments on the owning config (same count as the tenant routing page); null on the default layer. */
  readonly config_assigned_orgs?: number | null;
  /** Whether this target can take traffic at all; see {@link TargetProbeReachReason}. */
  readonly reachable?: boolean;
  readonly reach_reason?: TargetProbeReachReason;
  /** Manual-disable note / time / operator (0082). Null while enabled; omitted by older gateways. */
  readonly disabled_note?: string | null;
  readonly disabled_at?: string | null;
  readonly disabled_by?: string | null;
}

/** One `routing_leg_faults` row attached to a physical-leg listing (#3139). */
export interface TargetProbeLegFault {
  readonly fault_kind: string;
  readonly mode: 'shadow' | 'act';
  readonly tripped_at: string;
  /** guard-v2: `org` = a fault one org's own trip wrote (that org only). */
  readonly scope_kind: 'global' | 'org';
  /** The org of an `org` fault; absent for `global`. */
  readonly scope_id?: string;
  /** guard-v2: the probe profile that releases it (`short` / `long_stream`). */
  readonly recover_probe_profile?: string;
}

/** One physical leg (upstream × account × bucket × channel × model) for a SKU. */
export interface TargetProbeLegRecord {
  readonly sku_id: string;
  readonly sku_name: string;
  readonly upstream_id: string;
  readonly upstream_name: string;
  readonly account_id: string;
  readonly key_bucket_id: string;
  readonly upstream_channel_id: string;
  readonly physical_model: string;
  readonly region: string;
  readonly members: readonly TargetProbeLegMember[];
  readonly tripped: boolean;
  readonly trip_reason: TargetProbeTripReason | null;
  readonly shadow_trip_reason: TargetProbeTripReason | null;
  /**
   * Active `routing_leg_faults` rows on this physical leg × SKU × region.
   * Silent trip/shadow lives here. Omitted by older gateways.
   */
  readonly faults?: readonly TargetProbeLegFault[];
  readonly shadow_tripped_at: string | null;
  readonly immune_until: string | null;
  readonly last_probe_at: string | null;
  readonly last_ok_at: string | null;
  readonly last_status_code: number | null;
  readonly last_error_excerpt: string | null;
  readonly next_probe_at: string | null;
  readonly backoff_level: number;
  /** Max across members in this region slice (the row that is closest to a trip / recovery). */
  readonly consecutive_failed_rounds: number;
  readonly consecutive_ok_rounds: number;
  /**
   * Earliest `tripped_at` among the slice's tripped member rows (0079): when
   * this leg was removed in this region. Null while not tripped; omitted by
   * older gateways.
   */
  readonly tripped_at?: string | null;
  /** The war progress of the slice's member rows (0084); omitted by older gateways. */
  readonly war_until?: string | null;
  readonly war_waves?: number | null;
  readonly war_last_failed?: number | null;
  readonly war_last_shots?: number | null;
  /** The region's probe level, from its latest `probe_level` event (last 24 h); null = normal. */
  readonly level?: ProbeLevel | null;
  /** When that level was entered. */
  readonly level_since?: string | null;
  /** War cooldown from the latest level / blocked event, and the wars in its window. */
  readonly war_cooldown_until?: string | null;
  readonly war_entries_window?: number | null;
}

export interface ListTargetProbeLegsResponse {
  readonly legs: readonly TargetProbeLegRecord[];
}

export interface ResetTargetProbeResponse {
  readonly ok: true;
  readonly cleared: boolean;
}

export type ProbeTargetNowRequest = TargetProbeTargetRef;

export interface ProbeTargetNowResponse {
  readonly ok: true;
  readonly state: TargetProbeStateRecord;
}

/**
 * `POST /internal/target-probes/schedule`. `due` / `probed` / `skipped` /
 * `in_flight` count physical legs; `tripped` / `recovered` count the member
 * state rows that tripped / recovered (a shared leg settles each member on
 * its own row). Rounds run in the gateway process, not inside the request:
 * `probed` is the rounds that finished before the response, `in_flight` the
 * ones started this tick that are still firing their spaced shots — their
 * trips and recoveries land in state and load events, not in this body.
 * `in_flight` is absent from an older gateway.
 */
export interface ScheduleTargetProbesResponse {
  readonly ok: true;
  readonly enabled: boolean;
  readonly due: number;
  readonly probed: number;
  readonly tripped: number;
  readonly recovered: number;
  readonly skipped: number;
  readonly in_flight?: number;
}

/**
 * `POST /internal/target-probes/try` — one shot with an operator-chosen probe
 * body, for "does this target answer at all". Works on disabled targets and
 * regardless of guaranteed-service config. Zero routing side effects: no
 * probe-state row, lease, load event, trip / shadow / immune change, trip
 * cache invalidation, fleet signal or Jev. The shot still writes its probe
 * usage-log row (non-tenant spend, ADR-003).
 */
export interface TryTargetProbeRequest extends TargetProbeTargetRef {
  readonly shape: 'ping' | 'arithmetic' | 'echo' | 'custom';
  /** Required when `shape` is `custom`; same rules as the guaranteed probe text. */
  readonly custom_text?: string | null;
  /** 1..64; omitted means 16. Only read for `custom`. */
  readonly custom_max_tokens?: number | null;
}

export interface TryTargetProbeResponse {
  readonly ok: true;
  /** Upstream HTTP status of the shot; 0 when the request never got an answer. */
  readonly status: number;
  readonly latency_ms: number;
  readonly output_tokens: number;
  /** Secret-scrubbed failure excerpt; null on a 2xx. */
  readonly excerpt: string | null;
  readonly upstream_name: string;
  readonly physical_model: string;
}
